import { Bar, Label, Legend, Line, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { selectDarkMode, selectDatesRange, selectPeriod } from "../../../state/selectors";
import { getState } from "../../../state/store";

import { colors, labelsAppearance, strCode } from "../../../consts";
import { t } from "../../../services";
import { rumEventsUtil } from "../../../utils";
import metricsColorHelpers from "./metricsColorHelpers";

import ChartTooltip from "../components/ChartTooltip";
import ChartLegend from "../components/ChartLegend";
import CustomXAxisTick from "../components/CustomXAxisTick";

const getRightYAxisMetricId = (metricsConfig) => {
  let rightYAxisMetricId;

  if (metricsConfig) {
    Object.keys(metricsConfig).forEach((metric) => {
      if (metricsConfig[metric]?.isRightYAxis) {
        rightYAxisMetricId = metricsConfig[metric].id;
      }
    });
  }

  return rightYAxisMetricId;
};

const getLines = ({ metrics, metricsConfig, opacity, isErrorMetrics }) => {
  const colors = metricsColorHelpers.updateColorsOrder(metrics, metricsConfig, isErrorMetrics);
  const rightYAxisMetricId = getRightYAxisMetricId(metricsConfig);

  return (
    metrics &&
    metrics?.map((metric, index) => {
      const color = metricsColorHelpers.getColor(colors, index, metric, metricsConfig);
      const yAxisId = rightYAxisMetricId === metric ? "right" : "left";

      return (
        <Line
          key={metric}
          type="linear"
          dataKey={metric}
          yAxisId={yAxisId}
          stroke={color}
          strokeOpacity={opacity && opacity[metric]}
          dot={{ stroke: color, r: 2, fill: color }}
          activeDot={{ strokeWidth: 0 }}
          animationDuration={400}
          isAnimationActive={false}
        />
      );
    })
  );
};

const getBars = ({ metrics, metricsConfig, opacity, isErrorMetrics }) => {
  const colors = metricsColorHelpers.updateColorsOrder(metrics, metricsConfig, isErrorMetrics);

  return (
    metrics &&
    metrics?.map((metric, index) => {
      const color = metricsColorHelpers.getColor(colors, index, metric, metricsConfig);

      return (
        <Bar
          key={metric}
          dataKey={metric}
          yAxisId={"left"}
          stackId="bar"
          fill={color}
          opacity={opacity && opacity[metric]}
          barSize={8}
          barCategoryGap={0}
          isAnimationActive={true}
          animationDuration={400}
        />
      );
    })
  );
};

const getChartAxes = ({
  data,
  maxY,
  yAxisLabel,
  yAxisUnit,
  isBarChart,
  metricsConfig,
  id,
  isCustomTicks,
  isTooltipHidden,
}) => {
  const darkMode = selectDarkMode()(getState());

  const rightYAxisMetricId = getRightYAxisMetricId(metricsConfig);

  const period = selectPeriod()(getState());
  const range = selectDatesRange()(getState());

  const { formatFn, appearance } = rumEventsUtil.getDateRangeConfig(period, range);

  let angle = 0;
  let tickMargin = 9;
  let fontSize = 10;

  if (!isCustomTicks) {
    switch (appearance) {
      case labelsAppearance.ANGLED: {
        angle = 45;
        tickMargin = 16;

        if (formatFn === "formatHour") {
          fontSize = 6;
        }
        break;
      }
      case labelsAppearance.VERTICAL: {
        angle = 90;
        tickMargin = 20;
        fontSize = 6;
        break;
      }
      default: {
        angle = 0;
      }
    }
  }

  const xAxisDataKey = data && Object.keys(data[0])[0];

  return (
    <>
      <XAxis
        dataKey={xAxisDataKey}
        stroke={`${darkMode ? colors.WHITE : colors.RAISIN_100}`}
        tickSize={4}
        tickMargin={tickMargin}
        angle={angle}
        interval={0}
        axisLine={{ stroke: "rgba(174, 172, 177, 0.5)" }}
        tickLine={{ stroke: "rgba(174, 172, 177, 0.5)" }}
        style={{ fontSize }}
        tick={isCustomTicks && <CustomXAxisTick />}
      />
      <YAxis
        className={maxY === 0 ? "rechart-zero-maxY-value" : ""}
        yAxisId={"left"}
        stroke={`${darkMode ? colors.WHITE : colors.RAISIN_100}`}
        domain={maxY && [0, maxY]}
        unit={yAxisUnit}
        tickSize={4}
        tickMargin={16}
        tickCount={2}
        axisLine={false}
        tickLine={{ stroke: "rgba(174, 172, 177, 0.5)" }}>
        <Label
          style={{
            fontWeight: 700,
            fontSize: "10px",
            lineHeight: "14px",
            fill: darkMode ? colors.WHITE : colors.RAISIN_100
          }}
          position="top"
          offset={22}
          angle={0}
          value={t(yAxisLabel)}
        />
      </YAxis>

      {rightYAxisMetricId && (
        <YAxis
          orientation="right"
          domain={[0, 1]}
          stroke={`${darkMode ? colors.WHITE : colors.RAISIN_100}`}
          yAxisId={"right"}
          tickCount={2}
          dataKey={rightYAxisMetricId}
          unit={"s"}
          axisLine={false}
          tickLine={false}>
          <Label
            style={{
              fontWeight: 700,
              fontSize: "10px",
              lineHeight: "14px",
              fill: darkMode ? colors.WHITE : colors.RAISIN_100
            }}
            position="top"
            offset={22}
            dx={-6}
            angle={0}
            value={t(strCode.MAX_AVG_SPEED_LABEL)}
          />
        </YAxis>
      )}

      <ReferenceLine
        yAxisId="left"
        y={maxY}
        stroke="rgba(174, 172, 177, 0.5)"
        className={maxY === 0 ? "rechart-zero-maxY-line" : ""}
      />

      <Tooltip
        wrapperStyle={{ display: isTooltipHidden ? 'none' : 'block' }}
        content={<ChartTooltip id={id} metricsConfig={metricsConfig} />}
        cursor={{ stroke: "rgba(174, 172, 177, 0.5)", opacity: isBarChart ? 0.3 : 1 }}
      />
    </>
  );
};

const getChartLegend = ({ metricsConfig, opacity, setOpacity }) => {
  return (
    <Legend
      content={
        <ChartLegend metricsConfig={metricsConfig} opacity={opacity} setOpacity={setOpacity} />
      }
      wrapperStyle={{ position: "relative" }}
    />
  );
};

const chartHelpers = {
  getLines,
  getBars,
  getChartAxes,
  getChartLegend
};

export default chartHelpers;
